import { useState, type ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { classNames } from '../utils/helpers';
import { useIsMobile } from '../hooks/useUI';

export function Header({ onAdminClick }: { onAdminClick?: () => void }) {
  const { session, logout, isAdmin, isAuthenticated } = useAuth();
  const isMobile = useIsMobile();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="header" role="banner">
      <div className="header-container container">
        <Link to="/" className="header-logo" aria-label="Zur Startseite">
          <span className="header-title">TourDeTirol 2026 Bilder</span>
        </Link>

        <nav className={classNames('header-nav', { 'header-nav--open': mobileMenuOpen })} role="navigation" aria-label="Hauptnavigation">
          {isAuthenticated && (
            <>
              <NavLink
                to="/gallery"
                className={({ isActive }) => classNames('header-link', { 'header-link--active': isActive })}
                onClick={() => setMobileMenuOpen(false)}
              >
                Galerie
              </NavLink>
              {isAdmin && (
                <button
                  className="header-link"
                  onClick={() => {
                    onAdminClick?.();
                    setMobileMenuOpen(false);
                  }}
                >
                  Admin
                </button>
              )}
            </>
          )}
        </nav>

        <div className="header-actions">
          {isAuthenticated ? (
            <div className="header-user">
              <span className="header-user-name">{session?.displayName || 'Gast'}</span>
              <button className="btn btn-ghost btn-sm" onClick={logout}>
                Abmelden
              </button>
            </div>
          ) : (
            <Link to="/login" className="btn btn-primary btn-sm">
              Anmelden
            </Link>
          )}

          {isMobile && (
            <button
              className="btn btn-ghost btn-icon header-menu-toggle"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-expanded={mobileMenuOpen}
              aria-controls="header-nav"
              aria-label={mobileMenuOpen ? 'Menü schließen' : 'Menü öffnen'}
            >
              {mobileMenuOpen ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <line x1="3" y1="12" x2="21" y2="12"></line>
                  <line x1="3" y1="6" x2="21" y2="6"></line>
                  <line x1="3" y1="18" x2="21" y2="18"></line>
                </svg>
              )}
            </button>
          )}
        </div>
      </div>
    </header>
  );
}

export function Main({ children }: { children: ReactNode }) {
  return <main className="main" role="main">{children}</main>;
}

export function Footer() {
  return (
    <footer className="footer" role="contentinfo">
      <div className="container">
        <p className="footer-text">
          TourDeTirol 2026 Bilder &mdash; Private Foto-Cloud für Freunde und Familie
        </p>
      </div>
    </footer>
  );
}

export function Layout({ children, showHeader = true, showFooter = true }: { children: ReactNode; showHeader?: boolean; showFooter?: boolean }) {
  return (
    <div className="layout">
      {showHeader && <Header />}
      <Main>{children}</Main>
      {showFooter && <Footer />}
    </div>
  );
}